import {
  Color,
  GeomItem,
  Lines,
  Cuboid,
  Points,
  Material,
  PointsMaterial,
  LinesMaterial,
  Operator,
  OperatorOutputMode,
  XfoOperatorOutput,
  Xfo,
  Vec3Attribute,
  BaseGeom,
} from '@zeainc/zea-engine'
import { XfoTrack } from './XfoTrack'
import { IndexEvent } from './BaseTrack'
import { HandleMaterial } from '@zeainc/zea-ux'

/** An operator for aiming items at targets.
 * @extends Operator
 */
class KeyDisplayOperator extends Operator {
  keyLocalOutput = new XfoOperatorOutput('KeyLocal', OperatorOutputMode.OP_WRITE)
  track: XfoTrack
  keyIndex: number
  /**
   * Create a gears operator.
   * @param {XfoTrack} track - The track value.
   * @param {number} keyIndex - The index of the key in the track
   */
  constructor(track: XfoTrack, keyIndex: number) {
    super()

    this.addOutput(this.keyLocalOutput)

    this.track = track
    this.keyIndex = keyIndex
    this.track.on('keyChanged', (event: IndexEvent) => {
      if (event.index == this.keyIndex) this.setDirty()
    })
    this.track.on('keyRemoved', (event: IndexEvent) => {
      const { index } = event
      if (this.keyIndex >= index) {
        this.setDirty()
      }
    })
    this.track.on('keyAdded', (event: IndexEvent) => {
      const { index } = event
      if (this.keyIndex >= index) {
        this.setDirty()
      }
    })
  }

  backPropagateValue(value: any): void {
    this.track.setKeyValue(this.keyIndex, value)
  }

  /**
   * The evaluate method.
   */
  evaluate(): void {
    this.keyLocalOutput.setClean(this.track.getKeyValue(this.keyIndex))
  }
}

/** An operator for aiming items at targets.
 * @extends Operator
 */
class XfoTrackDisplay extends GeomItem {
  track: XfoTrack
  dotsItem: GeomItem
  keyMat: Material
  keyCube: Cuboid
  keys: GeomItem[]
  /**
   * Create a TrackDisplay operator.
   * @param {string} name - The name value.
   * @param {XfoTrack} track - The track to display.
   */
  constructor(track: XfoTrack) {
    super(track.getName())

    this.track = track

    this.getParameter('Geometry').setValue(new Lines())

    const linesMat = new LinesMaterial('trackLine')
    linesMat.baseColorParam.setValue(new Color(0.3, 0.3, 0.3))
    linesMat.getParameter('Overlay').setValue(0.5)
    this.getParameter('Material').setValue(linesMat)

    const dotsMat = new PointsMaterial('trackDots')
    dotsMat.baseColorParam.setValue(new Color(0.75, 0.75, 0.75))
    dotsMat.getParameter('Overlay').setValue(0.5)
    this.dotsItem = new GeomItem('dots', new Points(), dotsMat)
    this.addChild(this.dotsItem)

    this.keyMat = new HandleMaterial('trackLine')
    this.keyMat.getParameter('MaintainScreenSize').setValue(1)
    this.keyMat.getParameter('Overlay').setValue(0.5)
    this.keyCube = new Cuboid(0.004, 0.004, 0.004)

    this.keys = []
    this.updatePath()
    this.displayKeys()

    this.track.on('keyAdded', (event: IndexEvent) => {
      this.displayKeys()
      this.updatePath()
    })
    this.track.on('keyRemoved', (event: IndexEvent) => {
      const { index } = event
      const handle = this.keys.pop()
      this.removeChild(this.getChildIndex(handle))
      this.displayKeys()
      this.updatePath()
    })
    this.track.on('keyChanged', (event: IndexEvent) => {
      this.updatePath()
    })
    this.track.on('loaded', () => {
      this.updatePath()
      this.displayKeys()
    })
  }

  displayKeys() {
    const displayKey = (index: number) => {
      if (!this.keys[index]) {
        const handle = new GeomItem('key' + index, this.keyCube, this.keyMat)
        this.addChild(handle)
        const keyDisplay = new KeyDisplayOperator(this.track, index)
        keyDisplay.keyLocalOutput.setParam(handle.localXfoParam)
        this.keys.push(handle)
      }
    }

    const numKeys = this.track.getNumKeys()
    for (let i = 0; i < numKeys; i++) {
      displayKey(i)
    }
  }

  updatePath(timeRange?: Array<number>) {
    const trackLines = <Lines>this.geomParam.value
    const trackDots = this.dotsItem.geomParam.value as BaseGeom

    const trackTimeRange = this.track.getTimeRange()
    if (Number.isNaN(trackTimeRange.x) || Number.isNaN(trackTimeRange.y)) return

    const numSamples = Math.round((trackTimeRange.y - trackTimeRange.x) / 50) // Display at 50 samples per second
    if (numSamples == 0) return

    trackLines.setNumVertices(numSamples + 1)
    trackLines.setNumSegments(numSamples)

    trackDots.setNumVertices(numSamples + 1)
    const linePositions = <Vec3Attribute>trackLines.getVertexAttribute('positions')
    const dotPositions = <Vec3Attribute>trackDots.getVertexAttribute('positions')
    for (let i = 0; i <= numSamples; i++) {
      if (i < numSamples) trackLines.setSegmentVertexIndices(i, i, i + 1)
      const time = trackTimeRange.x + (trackTimeRange.y - trackTimeRange.x) * (i / numSamples)

      const xfo: Xfo = this.track.evaluate(time)
      linePositions.setValue(i, xfo.tr)
      dotPositions.setValue(i, xfo.tr)
    }

    trackDots.setBoundingBoxDirty()
    trackDots.emit('geomDataTopologyChanged', {})

    trackLines.setBoundingBoxDirty()
    trackLines.emit('geomDataTopologyChanged', {})
  }
}

export { XfoTrackDisplay }
